// ============================================================================
// CHANGO V5.0 — D3 Customers List
// ============================================================================
// Simple listing + filter for the workspace's customer records. D6 will add
// timeline, tags, multi-contact expansion; D3's bar is "show the rows and
// let users click into them".
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import ExcelImportModal from '../../components/ExcelImportModal.jsx';

export default function CustomersList() {
  const [params, setParams] = useSearchParams();
  const qFilter = params.get('q') || '';
  const groupIdFilter = params.get('group_id') || '';

  const [rows, setRows]   = useState([]);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Load groups (for dropdown and chip display)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/customer-groups');
        setGroups(res?.items || []);
      } catch (err) {
        // Silently fail group load; list should still work
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (qFilter) query.set('q', qFilter);
    if (groupIdFilter) query.set('group_id', groupIdFilter);
    query.set('limit', '50');
    (async () => {
      try {
        const res = await api.get(`/api/customers?${query.toString()}`);
        if (cancelled) return;
        setRows(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number.isInteger(res?.total) ? res.total : 0);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof HttpError && err.status === 404) {
          setRows([]); setTotal(0);
        } else {
          setError(err.message || '加载客户列表失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [qFilter, groupIdFilter]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else       next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">客户</h1>
          <p className="text-sm text-slate-500">共 {total} 条</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            placeholder="搜索公司名/联系人…"
            defaultValue={qFilter}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setFilter('q', e.currentTarget.value.trim());
            }}
            className="input-text w-60"
          />
          <select
            value={groupIdFilter}
            onChange={(e) => setFilter('group_id', e.target.value)}
            className="input-text"
          >
            <option value="">全部分组</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          <Link to="/app/customers/groups" className="btn-ghost text-sm">
            客户分组
          </Link>
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary"
          >
            批量导入
          </button>
          <Link to="/app/customers/new" className="btn-primary">
            新建客户
          </Link>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">公司</th>
              <th className="px-4 py-3">国家/地区</th>
              <th className="px-4 py-3">主联系人</th>
              <th className="px-4 py-3">行业</th>
              <th className="px-4 py-3">跟进人</th>
              <th className="px-4 py-3">最近联系</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">加载中…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  暂无客户 · <Link to="/app/customers/new" className="text-chango-700 hover:underline">创建第一条</Link>
                </td>
              </tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <Link to={`/app/customers/${r.id}`} className="text-chango-700 hover:underline">
                    {r.company_name || '—'}
                  </Link>
                </td>
                <td className="px-4 py-3 text-slate-600">{r.country_code || '—'}</td>
                <td className="px-4 py-3">{r.primary_contact_name || '—'}</td>
                <td className="px-4 py-3 text-slate-600">{r.industry || '—'}</td>
                <td className="px-4 py-3 text-slate-600">{r.owner_display_name || r.owner_name || r.owner_email || '—'}</td>
                <td className="px-4 py-3 text-slate-500">{r.last_contacted_at || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showImportModal && (
        <ExcelImportModal
          entity="customers"
          onClose={() => setShowImportModal(false)}
          onComplete={() => {
            setShowImportModal(false);
            // 刷新列表
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
